// SEO-friendly conversion routes generator
// This generates all possible conversion combinations for sitemap and SEO

// SITE_ORIGIN is imported AND re-exported below: `export ... from` alone makes
// the name available to importers without binding it in this module's own
// scope, and generateSitemapEntries uses it directly.
import { SITE_ORIGIN } from './siteOrigin.js';
import { BLOG_BASE, getBlogPaths } from './blogRoutes.js';

export const formatCategories = {
  image: ['jpg', 'jpeg', 'png', 'webp', 'avif', 'gif', 'bmp', 'tiff', 'ico'],
  video: ['mp4', 'webm', 'avi', 'mov', 'mkv', 'flv', 'wmv', 'mpeg', 'gif'],
  document: ['pdf', 'doc', 'docx', 'txt', 'html', 'md', 'rtf']
};

// Generate all possible conversion combinations
export const generateConversionRoutes = () => {
  const routes = [];

  Object.entries(formatCategories).forEach(([category, formats]) => {
    formats.forEach(fromFormat => {
      formats.forEach(toFormat => {
        if (fromFormat !== toFormat) {
          routes.push({
            path: `/convert/${fromFormat}-to-${toFormat}`,
            fromFormat,
            toFormat,
            category,
            title: `Convert ${fromFormat.toUpperCase()} to ${toFormat.toUpperCase()} - Free Online Converter`,
            description: `Free online ${fromFormat.toUpperCase()} to ${toFormat.toUpperCase()} converter. Convert your files quickly and securely in your browser. No upload required, 100% private.`,
            keywords: [
              `${fromFormat} to ${toFormat}`,
              `convert ${fromFormat} to ${toFormat}`,
              `${fromFormat} ${toFormat} converter`,
              `online ${fromFormat} converter`,
              `free ${fromFormat} to ${toFormat}`,
              `${category} converter`,
              `browser converter`,
              'free converter',
              'online converter'
            ]
          });
        }
      });
    });
  });

  return routes;
};

// Popular conversion combinations (for featured/quick access)
export const popularConversions = [
  // Images
  { from: 'jpg', to: 'png', category: 'image' },
  { from: 'png', to: 'jpg', category: 'image' },
  { from: 'jpg', to: 'webp', category: 'image' },
  { from: 'png', to: 'webp', category: 'image' },
  { from: 'webp', to: 'png', category: 'image' },
  { from: 'webp', to: 'jpg', category: 'image' },
  { from: 'jpg', to: 'avif', category: 'image' },
  { from: 'png', to: 'avif', category: 'image' },
  { from: 'webp', to: 'avif', category: 'image' },
  { from: 'avif', to: 'png', category: 'image' },
  { from: 'avif', to: 'jpg', category: 'image' },
  { from: 'gif', to: 'mp4', category: 'video' },
  { from: 'png', to: 'ico', category: 'image' },

  // Videos
  { from: 'mp4', to: 'webm', category: 'video' },
  { from: 'webm', to: 'mp4', category: 'video' },
  { from: 'avi', to: 'mp4', category: 'video' },
  { from: 'mov', to: 'mp4', category: 'video' },
  { from: 'mkv', to: 'mp4', category: 'video' },
  { from: 'mp4', to: 'gif', category: 'video' },

  // Documents
  { from: 'pdf', to: 'txt', category: 'document' },
  { from: 'pdf', to: 'docx', category: 'document' },
  { from: 'txt', to: 'pdf', category: 'document' },
  { from: 'docx', to: 'pdf', category: 'document' },
  { from: 'doc', to: 'pdf', category: 'document' },
  { from: 'txt', to: 'docx', category: 'document' },
  { from: 'txt', to: 'doc', category: 'document' },
  { from: 'docx', to: 'txt', category: 'document' },
  { from: 'doc', to: 'txt', category: 'document' },
  { from: 'md', to: 'docx', category: 'document' },
  { from: 'html', to: 'docx', category: 'document' },
  { from: 'html', to: 'txt', category: 'document' },
  { from: 'md', to: 'html', category: 'document' },
  { from: 'txt', to: 'md', category: 'document' }
];

// Get conversion info by route
export const getConversionInfo = (conversionType) => {
  if (!conversionType) return null;

  const parts = conversionType.split('-to-');
  if (parts.length !== 2) return null;

  const [fromFormat, toFormat] = parts;

  // Find category. `gif` appears in BOTH the image and video lists, and this
  // loop has no early exit, so it used to resolve to whichever category came
  // last (video) for every gif pair — right for gif-to-mp4, wrong for
  // gif-to-png. Take the first match, and let the target format break the tie
  // for a format that legitimately lives in two categories.
  const categoriesFor = (fmt) =>
    Object.entries(formatCategories)
      .filter(([, formats]) => formats.includes(fmt))
      .map(([cat]) => cat);

  const fromCategories = categoriesFor(fromFormat);
  const category =
    fromCategories.find((cat) => formatCategories[cat].includes(toFormat)) ||
    fromCategories[0] ||
    null;

  if (!category) return null;

  return {
    fromFormat,
    toFormat,
    category,
    title: `Convert ${fromFormat.toUpperCase()} to ${toFormat.toUpperCase()} - Free Online Converter`,
    description: `Free online ${fromFormat.toUpperCase()} to ${toFormat.toUpperCase()} converter. Convert your files quickly and securely in your browser. No upload required, 100% private.`
  };
};

// The site's own origin. Used for sitemap/canonical generation, so a domain
// change is one edit rather than a grep. (This replaced a `https://yourdomain.com`
// placeholder that had shipped into generateSitemapEntries.)
//
// Defined in siteOrigin.js and re-exported here so the many existing importers
// keep working: blogRoutes.js also needs it, and this module imports
// getBlogPaths from blogRoutes, so the constant cannot live on either side of
// that pair without creating an import cycle.
export { SITE_ORIGIN };

// All free tools live under one base path.
//
// They were previously scattered at the root (/convert, /clip-video, ...),
// which said nothing about how they relate to each other. Grouping them under
// /free-tools makes the hub page the parent of its tools rather than a sibling,
// which is both clearer to a reader and a cleaner signal to a crawler — the
// URL now describes the hierarchy the breadcrumbs already claimed.
//
// Defined once here because four things must agree on it: the router, every
// internal link, the sitemap, and the prerenderer.
export const FREE_TOOLS_BASE = '/free-tools';

export const TOOL_PATHS = {
  hub: FREE_TOOLS_BASE,
  convert: `${FREE_TOOLS_BASE}/convert`,
  clipVideo: `${FREE_TOOLS_BASE}/clip-video`,
  compress: `${FREE_TOOLS_BASE}/compress`,
  // Split by media type: the two tools share a shell but target different
  // searches ("compress image" vs "compress video"), and each page carries its
  // own copy. A ?type= param could not rank for either.
  compressImage: `${FREE_TOOLS_BASE}/compress/image`,
  compressVideo: `${FREE_TOOLS_BASE}/compress/video`,
  subtitles: `${FREE_TOOLS_BASE}/subtitle-generator`,
  formatGuide: `${FREE_TOOLS_BASE}/format-guide`,
};

export const conversionPath = (slug) => `${TOOL_PATHS.convert}/${slug}`;

// Contact. Defined here with the other paths for the same reason they are: the
// router, the footer link, the sitemap and the prerenderer must agree, and a
// page that four files spell independently is a page one of them will
// eventually spell wrong.
export const CONTACT_PATH = '/contact';

// The address the contact page publishes. One definition so the mailto link,
// the visible text and the page's structured data cannot drift apart.
export const CONTACT_EMAIL = 'max@subscene.io';

// Old root-level paths -> their new home. Used by server.js to 301 rather than
// 404: these URLs are already in the sitemap Google was given, and may be
// linked externally, so breaking them would discard whatever equity exists and
// hand visitors a dead end.
export const LEGACY_PATH_REDIRECTS = {
  '/convert': TOOL_PATHS.convert,
  '/clip-video': TOOL_PATHS.clipVideo,
  '/compress': TOOL_PATHS.compress,
  '/compress-image': `${FREE_TOOLS_BASE}/compress/image`,
  '/compress-video': `${FREE_TOOLS_BASE}/compress/video`,
  '/subtitle-generator': TOOL_PATHS.subtitles,
  '/format-guide': TOOL_PATHS.formatGuide,
};

// THE INDEXABLE SET.
//
// generateConversionRoutes() produces 186 combinations, which is the right
// vocabulary for the in-app picker but the wrong thing to put in a sitemap: the
// large majority differ only by two substituted format names, and several are
// nonsense nobody searches for (ico-to-tiff, wmv-to-flv, rtf-to-md). Google
// crawls a handful of a template like that, infers the pattern and drops the
// rest — and thin-page volume is a site-level quality signal, so the 154 drag
// down the 32 that could rank.
//
// So indexing is opt-in: only pairs listed in popularConversions are canonical,
// sitemapped and prerendered. Everything else still WORKS — the route renders
// and converts exactly as before — it is just marked noindex,follow so its link
// equity flows to the hub instead of competing with it.
const curatedSlugs = new Set(
  popularConversions.map(({ from, to }) => `${from}-to-${to}`)
);

export const isCuratedConversion = (conversionType) =>
  curatedSlugs.has(String(conversionType || '').toLowerCase());

// The conversion paths that belong in the sitemap and get prerendered.
export const getCuratedConversionPaths = () =>
  popularConversions.map(({ from, to }) => conversionPath(`${from}-to-${to}`));

// Related curated conversions, for internal linking.
//
// Internal links are the one link-building lever entirely within our control,
// and these pages had none: each was an orphan reachable only from the sitemap,
// with no path for authority to flow between them. Relevance is ranked so the
// links are genuinely useful rather than a footer dump — the reverse conversion
// first (someone converting JPG to PNG plausibly wants the other direction),
// then pairs sharing a format, then others in the same category.
export const getRelatedConversions = (fromFormat, toFormat, limit = 6) => {
  if (!fromFormat || !toFormat) return [];
  const self = `${fromFormat}-to-${toFormat}`;

  const scored = popularConversions
    .map(({ from, to, category }) => ({ from, to, category, slug: `${from}-to-${to}` }))
    .filter((c) => c.slug !== self)
    .map((c) => {
      let score = 0;
      if (c.from === toFormat && c.to === fromFormat) score += 100; // the reverse
      if (c.from === fromFormat) score += 40;                       // same source
      if (c.to === toFormat) score += 30;                           // same target
      if (c.from === toFormat || c.to === fromFormat) score += 15;  // shares a format
      return { ...c, score };
    })
    .filter((c) => c.score > 0)
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, limit).map((c) => ({
    path: conversionPath(c.slug),
    label: `${c.from.toUpperCase()} to ${c.to.toUpperCase()}`,
    slug: c.slug,
  }));
};

// Every public URL that should be crawled, in priority order. Single source of
// truth shared by the sitemap generator and scripts/prerender.js, so the two can
// never disagree about what exists.
//
// Deliberately excluded: /login (robots.txt disallows it, and listing a
// disallowed URL in a sitemap is a Search Console error) and /free-tools/compress
// (it 301s to the image page, and a sitemap entry that redirects is a crawl
// error rather than a page).
//
// / is the product page and /free-tools is the tools hub — two distinct pages
// targeting two distinct searches, each canonical to itself. /landing renders
// the same component as / and canonicalises to it; it stays listed only because
// it is already indexed, at a low priority so it is not mistaken for a document
// of its own.
export const getIndexableRoutes = () => [
  { path: '/', priority: '1.0', changefreq: 'weekly' },
  { path: TOOL_PATHS.hub, priority: '0.95', changefreq: 'weekly' },
  { path: '/landing', priority: '0.5', changefreq: 'monthly' },
  // Contact. Low priority — it is a utility page rather than something that
  // targets a search — but indexable, because "contact <product>" is a real
  // query and a site with no reachable contact page reads as abandoned.
  { path: CONTACT_PATH, priority: '0.4', changefreq: 'yearly' },
  { path: TOOL_PATHS.convert, priority: '0.9', changefreq: 'weekly' },
  // A reference page rather than a tool. It exists to be cited — technical
  // comparison tables are what developers actually link to — and it is the
  // natural hub linking out to the individual conversion pages.
  { path: TOOL_PATHS.formatGuide, priority: '0.9', changefreq: 'monthly' },
  { path: TOOL_PATHS.subtitles, priority: '0.9', changefreq: 'weekly' },
  { path: TOOL_PATHS.clipVideo, priority: '0.9', changefreq: 'weekly' },
  // /free-tools/compress is deliberately NOT here: it redirects to the image
  // page, and a sitemap entry that 301s is a crawl error rather than a page.
  // The two type pages are the documents.
  { path: TOOL_PATHS.compressImage, priority: '0.9', changefreq: 'weekly' },
  { path: TOOL_PATHS.compressVideo, priority: '0.9', changefreq: 'weekly' },
  ...getCuratedConversionPaths().map((path) => ({
    path,
    priority: '0.8',
    changefreq: 'monthly',
  })),
  // Guides. The hub plus one URL per post — see utils/blogRoutes.js.
  //
  // Imported rather than listed here so the article set has exactly one
  // definition (src/data/blog/index.js), which the hub page, the related-post
  // ranking, the sitemap and the prerenderer all read.
  ...getBlogPaths().map((path) => ({
    path,
    priority: path === BLOG_BASE ? '0.8' : '0.7',
    changefreq: 'monthly',
  })),
];

// Generate sitemap entries for the indexable routes only.
// lastmod is deliberately NOT set here. It is derived from git history by
// scripts/generate-sitemap.mjs, which can shell out; this module also runs in
// the browser and cannot. Stamping "now" on every route — which this used to do
// — marks all 42 URLs as freshly changed on every build, and Google ignores
// lastmod values it finds unreliable.
export const generateSitemapEntries = () =>
  getIndexableRoutes().map(({ path, changefreq, priority }) => ({
    loc: `${SITE_ORIGIN}${path}`,
    changefreq,
    priority,
  }));

// Total number of conversion combinations
export const getTotalConversions = () => {
  return generateConversionRoutes().length;
};

// Get conversions by category
export const getConversionsByCategory = (category) => {
  const routes = generateConversionRoutes();
  return routes.filter(route => route.category === category);
};
